import React, { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';

type NavItem = { name: string; path: string; external?: boolean };

const nav: NavItem[] = [
  { name: 'Home', path: '/' },
  { name: 'Menu', path: '/menu' },
  { name: 'About', path: '/about' },
  { name: 'Contact', path: '/contact' },
  { name: 'Bike Rentals', path: 'https://alljoybike.com', external: true },
  { name: 'AirBnB', path: '/airbnb' },
];

const Header: React.FC = () => {
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [showBikePopup, setShowBikePopup] = useState(true);
  const [scrolled, setScrolled] = useState(false);

  // Handle scroll state for header styling
  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Lock body scroll when menu open
  useEffect(() => {
    document.body.style.overflow = open ? 'hidden' : '';
    return () => { document.body.style.overflow = ''; };
  }, [open]);

  // Close on Escape key
  useEffect(() => {
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        setOpen(false);
        setShowBikePopup(false);
      }
    };
    document.addEventListener('keydown', handleKey);
    return () => document.removeEventListener('keydown', handleKey);
  }, []);

  const NavLink: React.FC<NavItem> = ({ name, path, external }) => {
    const active = pathname === path;

    const baseClasses = `
      relative px-3 py-2 text-sm font-medium rounded-lg
      transition-all duration-200 ease-smooth
    `;

    const activeClasses = active
      ? 'text-orange-500 bg-orange-50'
      : 'text-gray-700 hover:text-orange-500 hover:bg-orange-50/50';

    if (external) {
      return (
        <a
          href={path}
          target="_blank"
          rel="noopener noreferrer"
          className={`${baseClasses} ${activeClasses}`}
        >
          {name}
          <svg className="inline-block w-3 h-3 ml-1 opacity-50" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
          </svg>
        </a>
      );
    }

    return (
      <Link to={path} className={`${baseClasses} ${activeClasses}`}>
        {name}
        {active && (
          <span className="absolute bottom-0 left-3 right-3 h-0.5 bg-orange-500 rounded-full" />
        )}
      </Link>
    );
  };

  const MobileNavLink: React.FC<NavItem> = ({ name, path, external }) => {
    const active = pathname === path;

    const baseClasses = `
      flex items-center w-full px-4 py-3.5 text-base font-medium rounded-xl
      transition-all duration-200 ease-smooth
    `;

    const activeClasses = active
      ? 'text-orange-500 bg-orange-100'
      : 'text-gray-800 hover:bg-orange-50 active:bg-orange-100';

    if (external) {
      return (
        <a
          href={path}
          target="_blank"
          rel="noopener noreferrer"
          onClick={() => setOpen(false)}
          className={`${baseClasses} ${activeClasses} justify-between`}
        >
          {name}
          <svg className="w-4 h-4 opacity-40" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
          </svg>
        </a>
      );
    }

    return (
      <button
        onClick={() => { navigate(path); setOpen(false); }}
        className={`${baseClasses} ${activeClasses}`}
      >
        {name}
      </button>
    );
  };

  return (
    <>
      {/* Bike Rental Popup */}
      {showBikePopup && (
        <div className="fixed bottom-5 right-5 z-50 w-[calc(100%-2.5rem)] max-w-sm animate-fade-in-up">
          <div className="card-elevated overflow-hidden border border-orange-100">
            <div className="relative p-5">
              <button
                onClick={() => setShowBikePopup(false)}
                className="absolute top-3 right-3 p-1.5 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-full transition-colors"
                aria-label="Close announcement"
              >
                <svg className="h-4 w-4" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                </svg>
              </button>

              <a
                href="https://alljoybike.com"
                target="_blank"
                rel="noopener noreferrer"
                className="block group"
              >
                <div className="flex items-center gap-4">
                  <div className="flex-shrink-0 w-14 h-14 rounded-xl bg-orange-50 flex items-center justify-center overflow-hidden">
                    <img src="/bikelogo.png" alt="Alljoy Bike & Beach Rentals" className="h-10 w-auto" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-bold text-orange-500 group-hover:text-orange-600 transition-colors">
                      Now Open!
                    </p>
                    <p className="text-sm text-gray-600 mt-0.5">
                      Alljoy Bike & Beach Rentals is ready for your adventure
                    </p>
                  </div>
                </div>

                <div className="mt-4">
                  <span className="btn-primary btn-sm w-full text-sm">
                    Book Your Rental Now
                  </span>
                </div>
              </a>
            </div>
          </div>
        </div>
      )}

      {/* Header */}
      <header
        className={`
          fixed inset-x-0 top-0 z-50
          transition-all duration-300 ease-smooth
          ${scrolled ? 'glass shadow-nav' : 'bg-white/80 backdrop-blur-sm'}
        `}
      >
        <div className="container-base">
          <div className="flex h-16 sm:h-18 lg:h-16 items-center justify-between">
            {/* Logo */}
            <Link to="/" className="flex-shrink-0 group">
              <img
                src="/logo.png"
                alt="Alljoy Donut Co."
                className="h-14 lg:h-12 w-auto transition-transform duration-200 group-hover:scale-105"
              />
            </Link>

            {/* Desktop nav */}
            <nav className="hidden lg:flex items-center gap-1">
              {nav.map(item => <NavLink key={item.name} {...item} />)}
              <div className="ml-4 pl-4 border-l border-gray-200">
                <span className="badge-orange text-xs">
                  Fri – Sun 7 AM – 1 PM
                </span>
              </div>
            </nav>

            {/* Mobile: hours badge + hamburger */}
            <div className="flex items-center gap-3 lg:hidden">
              <span className="badge-orange text-xs hidden sm:flex">
                Fri – Sun 7 AM – 1 PM
              </span>
              <button
                aria-label="Toggle menu"
                aria-expanded={open}
                onClick={() => setOpen(v => !v)}
                className={`
                  relative p-2.5 rounded-xl transition-all duration-200
                  ${open ? 'bg-orange-100 text-orange-500' : 'hover:bg-gray-100 text-gray-700'}
                `}
              >
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" className="h-6 w-6">
                  {open ? (
                    <path d="M6 18L18 6M6 6l12 12" />
                  ) : (
                    <>
                      <path d="M4 8h16" />
                      <path d="M4 16h16" />
                    </>
                  )}
                </svg>
              </button>
            </div>
          </div>
        </div>
      </header>

      {/* Mobile Menu Backdrop */}
      <div
        className={`
          fixed inset-0 z-40 bg-black/40 backdrop-blur-sm
          transition-opacity duration-300 lg:hidden
          ${open ? 'opacity-100' : 'opacity-0 pointer-events-none'}
        `}
        onClick={() => setOpen(false)}
        aria-hidden="true"
      />

      {/* Mobile Sidebar */}
      <div
        className={`
          fixed top-0 right-0 bottom-0 z-50 w-80 max-w-[85vw]
          bg-white shadow-popup
          transform transition-transform duration-300 ease-smooth lg:hidden
          ${open ? 'translate-x-0' : 'translate-x-full'}
        `}
        role="dialog"
        aria-modal="true"
      >
        {/* Mobile menu header */}
        <div className="flex items-center justify-between h-16 px-5 border-b border-gray-100">
          <span className="text-lg font-semibold text-gray-900">Menu</span>
          <button
            onClick={() => setOpen(false)}
            className="p-2 rounded-xl hover:bg-gray-100 text-gray-500 transition-colors"
            aria-label="Close menu"
          >
            <svg className="h-5 w-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2}>
              <path d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Mobile nav items */}
        <nav className="flex flex-col p-4 space-y-1 overflow-y-auto" style={{ maxHeight: 'calc(100vh - 130px)' }}>
          {nav.map(item => <MobileNavLink key={item.name} {...item} />)}
        </nav>

        {/* Mobile menu footer */}
        <div className="absolute bottom-0 left-0 right-0 p-4 border-t border-gray-100 bg-gray-50">
          <div className="badge-orange w-full justify-center">
            Fri – Sun 7 AM – 1 PM
          </div>
        </div>
      </div>
    </>
  );
};

export default Header;
